import { DownloadIcon, TrashIcon } from '@radix-ui/react-icons';
import { Badge, Button, Flex, Heading, Skeleton, Text } from '@radix-ui/themes';
import { CsvExportHelper } from 'classes/helpers/csv-export.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonHitterAvatar } from 'components/common/hitters/avatar';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { PlayerStatusBadge } from 'components/common/hitters/player-status-badge';
import { CommonHitterSessionStats } from 'components/common/hitters/session-stats';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { SectionHeader } from 'components/sections/header';
import { lightFormat } from 'date-fns';
import format from 'date-fns-tz/format';
import parseISO from 'date-fns/parseISO';
import { t } from 'i18next';
import { IBreadcrumb } from 'interfaces/i-breadcrumbs';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_DATE_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISessionHitData } from 'lib_ts/interfaces/csv/exports/i-session-hits';
import { first } from 'lodash';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import useHittersStore from 'stores/use-hitters-store';

export const SessionHitsHeader = (props: {
  hitterID: string;
  sessionID: string;
  breadcrumbs: IBreadcrumb[];
  onDeleteSession: () => void;
}) => {
  const navigate = useNavigate();
  const hits = useAnalyticsStore((s) => s.hits);
  const sessions = useAnalyticsStore((s) => s.sessions);
  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);
  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);

  const hitters = useHittersStore((s) => s.hitters);
  const loadingHitters = useHittersStore((s) => s.loading);

  const session = useMemo(() => {
    if (!props.hitterID) {
      // return the session w/o any hitter info
      return sessions.find((s) => s._id === props.sessionID && !s.hitterID);
    }

    // return the session for the specified hitter
    return sessions.find(
      (s) => s._id === props.sessionID && s.hitterID === props.hitterID
    );
  }, [sessions, props.sessionID, props.hitterID]);

  const hitter = useMemo(
    () =>
      props.hitterID
        ? hitters.find((h) => h._id === props.hitterID)
        : undefined,
    [hitters, props.hitterID]
  );

  const [dialogConfirm, setDialogConfirm] = useState<number>();

  const handleOpenProfile = () => {
    if (!props.hitterID) {
      return;
    }

    navigate(paths.hitterLibrary.profile(props.hitterID));
  };

  const hitterName =
    hitter?.name ?? first(hits)?.hitter?.name ?? t('hitters.anonymous');

  return (
    <ErrorBoundary identifier="SessionHitsHeader">
      <SectionHeader header={null} breadcrumbs={props.breadcrumbs} />

      <Flex
        align="center"
        justify="between"
        wrap="wrap"
        gap={RADIX.FLEX.GAP.SM}
      >
        <Flex gap={RADIX.FLEX.GAP.MD} align="center">
          {hitter && (
            <CommonHitterAvatar
              key={hitter._id}
              hitter={hitter}
              size="7"
              onClick={handleOpenProfile}
            />
          )}
          <Flex direction="column" justify="center" gap={RADIX.FLEX.GAP.XS}>
            <Flex gap="2" align="center">
              <Skeleton loading={loadingHitters || !session || loading}>
                <Heading
                  size={RADIX.HEADING.SIZE.LG}
                  onClick={hitter ? handleOpenProfile : undefined}
                  className={hitter ? 'cursor-pointer' : ''}
                >
                  {hitterName}
                </Heading>
              </Skeleton>

              <Skeleton loading={!hitter || loadingHitters}>
                {hitter && <HitterSideBadge value={hitter.side} />}
              </Skeleton>

              <Skeleton loading={!hitter || loadingHitters}>
                <PlayerStatusBadge value={hitter?.statsApiData?.statusCode} />
              </Skeleton>

              <Skeleton loading={!session}>
                {session && (
                  <Badge color={RADIX.COLOR.NEUTRAL}>
                    {session.isInGame ? 'In-Game' : 'Practice'}
                  </Badge>
                )}
              </Skeleton>
            </Flex>

            <Skeleton loading={!session}>
              <Text color={RADIX.COLOR.SECONDARY}>
                {`${
                  session
                    ? format(
                        parseISO(session?.startDate),
                        LOCAL_DATETIME_FORMAT,
                        {
                          timeZone: LOCAL_TIMEZONE,
                        }
                      )
                    : undefined
                } · ${session?.shots ?? 0} ${t('common.shots')}`}
              </Text>
            </Skeleton>
          </Flex>
        </Flex>

        <Flex gap="2">
          <Button
            variant={RADIX.BUTTON.VARIANT.SECONDARY}
            color={RADIX.COLOR.DANGER}
            onClick={() => setDialogConfirm(Date.now())}
          >
            <TrashIcon /> {t('admin.delete-session')}
          </Button>
          <Button
            variant={RADIX.BUTTON.VARIANT.SECONDARY}
            onClick={() => {
              if (!session) {
                return;
              }

              const dateStamp = lightFormat(
                parseISO(session.startDate),
                LOCAL_DATE_FORMAT
              );

              const parts = [
                'Hitting Report',
                '-',
                hitter?.name ?? 'No Hitter',
                `(${dateStamp})`,
              ];

              // cleanup for nested fields so the CSV doesn't just show [object Object]
              const csvHits: ISessionHitData[] = hits
                .filter((m) => !m.isInGame)
                .map((m) => ({
                  created: m._created,
                  hitter: {
                    _id: m.hitter?._id ?? '',
                    customID: m.hitter?.customID ?? '',
                    name: m.hitter?.name ?? '',
                    level: m.hitter?.level ?? '',
                  },
                  fire: {
                    _id: m.fire._id,
                    machineID: m.fire.machineID,
                    session: m.fire.session,
                  },
                  pitch: {
                    _id: m.pitch?._id ?? '',
                    type: m.pitch?.type ?? '',
                    name: m.pitch?.name ?? '',
                    speedMPH: m.pitch?.speedMPH ?? '',
                    xBreakIN: m.pitch?.xBreakIN ?? '',
                    zBreakIN: m.pitch?.zBreakIN ?? '',
                    plateX: m.pitch?.plate?.plate_x ?? '',
                    plateZ: m.pitch?.plate?.plate_z ?? '',
                    releaseX: m.pitch?.release?.px ?? '',
                    releaseY: m.pitch?.release?.py ?? '',
                    releaseZ: m.pitch?.release?.pz ?? '',
                  },
                  hit: {
                    _id: m._id,
                    invalid: !!m.hit?.invalid,
                    swing: m.hit?.swing ?? '',
                    swing_override: m.hit?.swing_override ?? '',
                    exitMPH: m.hit?.exitMPH ?? '',
                    vLaunchDEG: m.hit?.vLaunchDEG ?? '',
                    hLaunchDEG: m.hit?.hLaunchDEG ?? '',
                    distanceFT: m.hit?.distanceFT ?? '',
                    outcome: m.hit?.outcome ?? '',
                    outcome_override: m.hit?.outcome_override ?? '',
                    hLandingDEG: m.hit?.hLandingDEG ?? '',
                  },
                  avg_excluded: m.avg_excluded,
                }));

              CsvExportHelper.saveCsv(csvHits, { prefix: parts.join(' ') });
            }}
          >
            <DownloadIcon /> {t('admin.export-session')}
          </Button>
        </Flex>
      </Flex>
      <CommonHitterSessionStats
        loading={loading}
        isMetric={!!sessionMachine?.isMetric}
        hits={hits}
      />

      {dialogConfirm && session && (
        <CommonConfirmationDialog
          key={dialogConfirm}
          identifier="ConfirmDeleteHitterSession"
          title="Confirm Deletion"
          content={
            <>
              <p>
                Are you sure you want to delete this session? This action cannot
                be undone.
              </p>
              <ul>
                <li>
                  {format(parseISO(session.startDate), LOCAL_DATETIME_FORMAT, {
                    timeZone: LOCAL_TIMEZONE,
                  })}
                  : {hitter?.name ?? t('hitters.anonymous')}
                </li>
              </ul>
            </>
          }
          action={{
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            onClick: async () => {
              await useAnalyticsStore.getState().deleteSessions([session._id]);

              props.onDeleteSession();
            },
          }}
        />
      )}
    </ErrorBoundary>
  );
};
